import { Panel } from '@studiometa/ui';

/**
 * @typedef {{}} PanelStoriesProps
 */

/**
 * PanelStories PanelStories.
 * @augments {Base<PanelStoriesProps>}
 */
export default class PanelStories extends Panel {
  currentIndex = 0;

  /**
   * Class config.
   * @type {import('@studiometa/js-toolkit').BaseConfig}
   */
  static config = {
    name: 'PanelStories',
    refs: ['progress[]', 'videos[]', 'prev[]', 'next[]', 'close'],
  };

  /**
   * Open Panel
   * @param  {number}  position current video to play
   * @returns {void}
   */
  openPanel(position) {
    this.$el.classList.remove('tw-opacity-0', 'tw-pointer-events-none');
    document.documentElement.classList.add('tw-overflow-hidden');

    for (let index = 0; index < this.$refs.progress.length; index++) {
      const progressBar = this.$refs.progress[index];

      if (index === position) {
        break;
      }
      progressBar.style.transform = 'scaleX(1)';
    }

    this.switchStep(position);
  }

  /**
   * Close panel
   * @returns {void}
   */
  closePanel() {
    this.$el.classList.add('tw-opacity-0', 'tw-pointer-events-none');
    document.documentElement.classList.remove('tw-overflow-hidden');

    this.currentVideo.pause();
    this.isPlaying = false;
    this.resetAllProgress();
  }

  /**
   * Close btn
   * @returns {void}
   */
  onCloseClick() {
    this.closePanel();
  }

  /**
   * [onClick description]
   * @returns {void}
   */
  onVideosClick() {
    if (this.isPlaying) {
      this.currentVideo.pause();
    } else {
      this.currentVideo.play();
    }

    this.isPlaying = !this.isPlaying;
  }

  /**
   * [ticked description]
   * @returns {void}
   */
  ticked() {
    if (this.isPlaying) {
      const percent = this.videoPosition / this.videoDuration;
      this.currentProgress.style.transform = `scaleX(${percent})`;

      if (percent === 1) {
        const position = this.currentIndex + 1;

        if (position > this.videosTotal) {
          this.isPlaying = false;
          this.showLastScreen();
          return;
        }

        this.switchStep(position);
      }
    }
  }

  /**
   * Switch step
   * @param  {void} position current position
   * @returns {void}
   */
  switchStep(position) {
    this.stopVideo();
    this.currentIndex = position;
    this.showVideo();
  }

  /**
   * [stopVideo description]
   * @returns {void}
   */
  stopVideo() {
    this.isPlaying = false;
    this.currentVideo.classList.add('tw-opacity-0');
    this.currentVideo.pause();
  }

  /**
   * [showVideo description]
   * @returns {void}
   */
  showVideo() {
    this.isPlaying = true;
    this.currentVideo.classList.remove('tw-opacity-0');
    this.currentVideo.play();
  }

  /**
   * Click on next btn
   * @returns {void}
   */
  onNextClick() {
    if (this.currentIndex === this.videosTotal) {
      this.closePanel();
      return;
    }

    this.currentVideo.currentTime = this.videoDuration;
    this.currentProgress.style.transform = `scaleX(1)`;
    const position = this.currentIndex + 1;
    this.switchStep(position);
  }

  /**
   * Click on prev btn
   * @returns {void}
   */
  onPrevClick() {
    this.currentVideo.currentTime = 0;
    this.currentProgress.style.transform = `scaleX(0)`;

    if (this.currentIndex === 0) {
      return;
    }

    const position = this.currentIndex - 1;
    this.switchStep(position);
  }

  /**
   * Reset all progressbar
   * @returns {void}
   */
  resetAllProgress() {
    this.$refs.progress.forEach((element) => {
      element.style.transform = `scaleX(0)`;
    });
    this.$refs.videos.forEach((element) => {
      element.currentTime = 0;
    });
  }

  /**
   * Close panel when type on Escape key
   * @param {object} props Props
   * @param {object} props.ESC Escape key
   * @returns {void}
   */
  keyed({ ESC }) {
    if (ESC) {
      this.closePanel();
    }
  }

  /**
   * Get current video
   * @returns {object} current Video
   */
  get currentVideo() {
    return this.$refs.videos[this.currentIndex];
  }

  /**
   * Current progress bar
   * @returns {object} current progress bar
   */
  get currentProgress() {
    return this.$refs.progress[this.currentIndex];
  }

  /**
   * Get Total duration of current video
   * @returns {number} Total duration of current video
   */
  get videoDuration() {
    return this.currentVideo.duration;
  }

  /**
   * Get current position of current video
   * @returns {number} Current position of current video
   */
  get videoPosition() {
    return this.currentVideo.currentTime;
  }

  /**
   * Total number of videos
   * @returns {number} Total number of videos
   */
  get videosTotal() {
    return this.$refs.videos.length - 1;
  }
}
